import * as css from 'css/exports.module.scss';

import './UptimeMonitorsDashboard.scss';

import { createAsyncSelectors } from '@lib/selectors';
import dayjs from 'dayjs';
import { FeatureBoundary } from '@ui/features';
import { getDirectParent } from '@lib/popups';
import { InfoCircleOutlined } from '@ant-design/icons';
import { SonarText } from '@ui/text';

import { Column, ColumnConfig } from '@ant-design/plots';
import { coreActions, DataSecurityMonitorType, DataSecurityUptimeMonitorPeriod, FeatureFlag } from '@sonar-software/sonar-core';
import { DATA_SECURITY_DASHBOARD, FORMATS } from '@sonar-software/sonar-ts-constants';
import React, { memo, useMemo } from 'react';
import { SonarCard, StatisticDelta } from '@ui/general';
import { Space, Statistic, Tooltip } from 'antd';
import { useAsyncStateWithPreload, useDataSecurityApps } from '@hooks';

type DayEntry = { day: number, up: 50 | 100, color: string };

const dashboardSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitorsDashboard.get);

function mapDowntimeDays(
	downtimeDays: DataSecurityUptimeMonitorPeriod['downtimeDays'],
	uptimeInterval: number
): Array<DayEntry> {
	const days: Array<DayEntry> = [];

	for (let index = 0; index < uptimeInterval; index++) {
		const down = downtimeDays.find((x) => x === index + 1);

		days.push({
			color: down ? css.deletion : css.lightGreen,
			day: index,
			up: down ? 50 : 100
		});
	}

	return days;
}

const chartConfig: ColumnConfig = {
	autoFit: true,
	color: ({ color }) => color,
	columnWidthRatio: 0.9,
	coordinate: [ {
		type: 'reflectX'
	  } ],
	data: [],
	height: 50,
	legend: false,
	padding: 0,
	seriesField: 'color',
	slider: false,
	tooltip: {
		customContent: (day) =>
			<div className='tooltip-container'>
				<Tooltip
					autoAdjustOverflow={ false }
					getPopupContainer={ getDirectParent }
					open
					placement='bottom'
					title={
						`${dayjs().subtract(Number(day), 'day')
							.format(FORMATS.SLASH_DATE)}`
					} />
			</div>,
		position: 'top'
	},
	xAxis: {
		label: {
			formatter: () => null
		},
		tickLine: { length: 0 }
	},
	xField: 'day',
	yAxis: {
		grid: null,
		label: {
			formatter: () => null
		}
	},
	yField: 'up'
};

interface UptimeMonitorsDashboardProps {
	applicationId?: number;
}

export const UptimeMonitorsDashboard: React.FC<UptimeMonitorsDashboardProps> = memo(({ applicationId }) => {
	const apps = useDataSecurityApps();

	const hasUptimeMonitors = useMemo(
		() => (
			applicationId ? apps.filter((app) => app.applicationId === applicationId) : apps
		).some(
			(app) => app.monitors.some((monitor) => monitor.monitorTypeId === DataSecurityMonitorType.Uptime)
		), [ applicationId, apps ]
	);

	const { data, isRequested } = useAsyncStateWithPreload(
		dashboardSelectors,
		coreActions.requestGetCoreDataSecurityUptimeMonitorsDashboard,
		true,
		{ applicationId }
	);

	if (!hasUptimeMonitors) {
		return null;
	}

	return (
		<FeatureBoundary feature={ FeatureFlag.DataSecurityDashboard }>
			<SonarCard
				id='uptime-montitors-dashboard'
				loading={ isRequested }
				title={ DATA_SECURITY_DASHBOARD.UPTIME_MONITORS }
				titleClassName='h2'
				useSpinnerLoading>
				<div className='monitor-periods'>
					{
						data?.map((monitor) =>
							<div
								className='monitor-period'
								key={ monitor.uptimeInterval }>
								<Statistic
									suffix={
										<StatisticDelta
											change={ monitor.changePercentage }
											isPercent
											precision={ 2 } />
									}
									title={
										<Space size={ 8 }>
											<SonarText className='monitor-title'>
												{ DATA_SECURITY_DASHBOARD.UPTIME_DAYS(monitor.uptimeInterval) }
											</SonarText>
											<Tooltip title={ DATA_SECURITY_DASHBOARD.UPTIME_TOOLTIP }>
												<InfoCircleOutlined />
											</Tooltip>
										</Space>
									}
									value={ monitor.uptimePercentage } />
								<Column
									{ ...chartConfig }
									data={ mapDowntimeDays(monitor.downtimeDays, monitor.uptimeInterval) } />
							</div>)
					}
				</div>
			</SonarCard>
		</FeatureBoundary>
	);
});
